/**
 * Pre-processes markdown to group consecutive code blocks with different
 * languages into a single "code-group" block for tabbed rendering.
 *
 * Example: A python block immediately followed by a bash block becomes:
 *   ```code-group:python|bash
 *   <python code>
 *   ---CODESPLIT---
 *   <bash code>
 *   ```
 */

const FENCED_BLOCK = /```(\w+)\n([\s\S]*?)```/g;

/**
 * Groups consecutive fenced code blocks with different languages.
 * Same-language consecutive blocks are left untouched.
 */
export function groupCodeBlocks(md: string): string {
	// Split markdown into segments: text and code blocks
	const segments: { type: "text" | "code"; lang: string; code: string; raw: string }[] = [];
	let lastIndex = 0;

	for (const match of md.matchAll(FENCED_BLOCK)) {
		const start = match.index;
		if (start > lastIndex) {
			segments.push({ type: "text", lang: "", code: "", raw: md.slice(lastIndex, start) });
		}
		segments.push({ type: "code", lang: match[1] ?? "", code: match[2] ?? "", raw: match[0] });
		lastIndex = start + match[0].length;
	}
	if (lastIndex < md.length) {
		segments.push({ type: "text", lang: "", code: "", raw: md.slice(lastIndex) });
	}

	// Group consecutive code blocks separated by at most whitespace
	const result: string[] = [];
	let i = 0;

	while (i < segments.length) {
		const seg = segments[i];

		if (seg.type !== "code") {
			result.push(seg.raw);
			i++;
			continue;
		}

		// Collect consecutive code blocks (may be separated by whitespace-only text)
		const group: { lang: string; code: string }[] = [{ lang: seg.lang, code: seg.code }];
		let j = i + 1;

		while (j < segments.length) {
			if (
				segments[j].type === "text" &&
				segments[j].raw.trim() === "" &&
				j + 1 < segments.length &&
				segments[j + 1].type === "code"
			) {
				const nextCode = segments[j + 1];
				// Only group if different language from ALL existing in group
				if (!group.some((g) => g.lang === nextCode.lang)) {
					group.push({ lang: nextCode.lang, code: nextCode.code });
					j += 2;
				} else {
					break;
				}
			} else {
				break;
			}
		}

		if (group.length === 1) {
			// Single block — keep as-is
			result.push(seg.raw);
		} else {
			// Multi-block — merge into code-group
			const langs = group.map((g) => g.lang).join("|");
			const code = group.map((g) => g.code).join("---CODESPLIT---\n");
			result.push(`\`\`\`code-group:${langs}\n${code}\`\`\``);
		}

		i = j;
	}

	return result.join("");
}
